define([
    'santa-components',
    'componentsCore',
    'lodash',
    'reactDOM',
    'skins',
    'paypalButton/skins/skins.json'
], function (santaComponents, componentsCore, _, ReactDOM, skinsPackage, skinsJson) {
    'use strict';

    const SantaTypes = santaComponents.santaTypesDefinitions;

    const BUY_BUTTON_LABEL = 'Buy now via PayPal';
    const DONATE_BUTTON_LABEL = 'Donate via PayPal';

    const LOCALES = {
        da: {image: 'da_DK', pixel: 'da_DK'},
        de: {image: 'de_DE', pixel: 'de_DE'},
        en: {image: 'en_US', pixel: 'en_US'},
        es: {image: 'es_ES', pixel: 'es_ES'},
        fr: {image: 'fr_FR', pixel: 'fr_FR'},
        it: {image: 'it_IT', pixel: 'it_IT'},
        ja: {image: 'ja_JP', pixel: 'ja_JP'},
        jp: {image: 'ja_JP', pixel: 'ja_JP'},
        nl: {image: 'nl_NL', pixel: 'nl_NL'},
        no: {image: 'no_NO', pixel: 'en_US'},
        pl: {image: 'pl_PL', pixel: 'pl_PL'},
        pt: {image: 'pt_BR', pixel: 'pt_BR'},
        ru: {image: 'ru_RU', pixel: 'en_US'},
        sv: {image: 'sv_SE', pixel: 'sv_SE'},
        tr: {image: 'tr_TR', pixel: 'tr_TR'}
    };

    const notifyUrl = 'https://inventory.wix.com/ecommerce/ipn/paypal';
    const buttonImageUrlTemplate = 'https://www.paypalobjects.com/${locale}/i/btn/btn_${buttonType}${showCreditCards}${buttonSize}.gif';
    const trackingImageUrlTemplate = 'https://www.paypalobjects.com/${locale}/i/scr/pixel.gif';

    function getLanguage(propLang, userLanguage) {
        if (propLang === 'userLang') {
            return userLanguage;
        }
        return propLang;
    }

    function getLocale(propLang, userLanguage) {
        const lang = getLanguage(propLang, userLanguage) || '';
        return LOCALES[lang.toLowerCase()] || LOCALES.en;
    }

    /**
     * Returns set of params based on weather its a buy button or a donation button
     *
     * @param compProp
     * @returns {Object}
     */
    function getFormParamsBasedOnButtonType(compProp) {
        const params = {};

        if (compProp.buttonType === 'buy') {
            params.cmdType = '_xclick';
            params.buildNotation = 'Wix_BuyNow_WPS_IL';
            params.itemName = compProp.itemName;
            params.itemNumber = compProp.itemID;
        } else {
            params.cmdType = '_donations';
            params.buildNotation = 'Wix_Donate_WPS_IL';
            params.itemName = compProp.organizationName;
            params.itemNumber = compProp.organizationID;
        }

        return params;
    }

    function getSubmitButtonAriaLabel(buttonType) {
        return buttonType === 'buy' ? BUY_BUTTON_LABEL : DONATE_BUTTON_LABEL;
    }

    /**
     * @class components.PayPalButton
     * @extends {core.skinBasedComp}
     */
    const paypalButton = {
        displayName: 'PayPalButton',
        mixins: [
            componentsCore.mixins.skinBasedComp
        ],

        propTypes: {
            compData: SantaTypes.Component.compData,
            compProp: SantaTypes.Component.compProp,
            currentUrl: SantaTypes.currentUrl,
            userLanguage: santaComponents.santaTypesDefinitions.WixUserSantaTypes.userLanguage.isRequired
        },

        onImageChanged() {
            this.registerReLayout();
            this.forceUpdate();
        },

        submitForm() {
            ReactDOM.findDOMNode(this.refs.form).submit();
        },

        handleSubmitImageKeyPress(event) {
            const SPACE_KEY = ' ';
            const ENTER_KEY = 'Enter';

            if (_.includes([SPACE_KEY, ENTER_KEY], event.key)) {
                this.submitForm();
                event.preventDefault();
            }
        },

        getSkinProperties() {
            const props = this.props;
            const formParams = getFormParamsBasedOnButtonType(props.compProp);
            const locale = getLocale(props.compProp.language, this.props.userLanguage);
            const currentUrlFull = props.currentUrl.full;
            const amount = props.compProp.amount;

            return {
                '': {
                    style: {
                        width: '',
                        height: ''
                    }
                },
                form: {
                    target: props.compProp.target
                },
                cmd: {value: formParams.cmdType},
                item_name: {value: formParams.itemName},
                item_number: {value: formParams.itemNumber},
                bn: {value: formParams.buildNotation},
                business: {value: props.compData.merchantID},
                currency_code: {value: props.compProp.currencyCode},
                notify_url: {value: notifyUrl},
                return: {value: currentUrlFull},
                cancel_return: {value: currentUrlFull},
                amount: {value: parseFloat(amount, 10) > 0 ? amount : undefined},
                trackingPixel: {
                    src: _.template(trackingImageUrlTemplate)({locale: locale.pixel}),
                    role: 'none'
                },
                submitImage: {
                    src: _.template(buttonImageUrlTemplate)({
                        locale: locale.image,
                        buttonType: props.compProp.buttonType === 'buy' ? 'buynow' : 'donate',
                        showCreditCards: props.compProp.showCreditCards && !props.compProp.smallButton ? 'CC' : '',
                        buttonSize: props.compProp.smallButton ? '_SM' : '_LG'
                    }),
                    onLoad: this.onImageChanged,
                    onClick: this.submitForm,
                    onKeyDown: componentsCore.utils.accessibility.keyboardInteractions.activateBySpaceOrEnterButton,
                    tabIndex: '0',
                    role: 'button',
                    'aria-label': getSubmitButtonAriaLabel(props.compProp.buttonType)
                }

            };
        }
    };

    componentsCore.compRegistrar.register('wysiwyg.viewer.components.PayPalButton', paypalButton);
    skinsPackage.skinsMap.addBatch(skinsJson);

    return paypalButton;
});
